import { Env } from '@stencil/core';
import { EnvironmentName } from '../types/quote';
import { HTTPError } from './httpError';

const ENDPOINT_MAP: Record<EnvironmentName, string> = {
  [EnvironmentName.PRODUCTION]: Env.SDK_PROXY_ENDPOINT,
  [EnvironmentName.SANDBOX]: Env.SDK_SANDBOX_PROXY_ENDPOINT,
};
const DEFAULT_HEADERS = { 'Content-Type': 'application/json' };

class API {
  private partnerID: string;
  private productID: string;
  private endpoint: string;
  private retryCount: number = 3;
  private retryDelay: number = 500;

  constructor(
    partnerID: string,
    productID: string,
    env: EnvironmentName = EnvironmentName.PRODUCTION
  ) {
    this.partnerID = partnerID;
    this.productID = productID;
    this.endpoint = ENDPOINT_MAP[env];
    if (!this.endpoint) throw new Error(`Invalid environment: ${env}`);
  }

  private trimLeadingSlash(path: string) {
    return path.charAt(0) === '/' ? path.slice(1) : path;
  }

  private fetchWithRetry = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ) => {
    for (let attempt = 0; attempt < this.retryCount - 1; attempt++) {
      const res = await fetch(input, init);
      if (res.status < 500) return res;
      const exponentialRetryDelay = Math.pow(2, attempt) * this.retryDelay;
      await new Promise((res) => setTimeout(res, exponentialRetryDelay));
    }
    return fetch(input, init);
  };

  private async returnOrThrow(response: Response) {
    if (response.status >= 200 && response.status < 300) {
      return response.json();
    } else {
      throw new HTTPError(
        JSON.stringify(await response.json()),
        response.status
      );
    }
  }

  async get(path: string) {
    const response = await this.fetchWithRetry(
      `${this.endpoint}/${this.partnerID}/${
        this.productID
      }/${this.trimLeadingSlash(path)}`
    );
    return this.returnOrThrow(response);
  }

  async post(path: string, body?: unknown) {
    const response = await this.fetchWithRetry(
      `${this.endpoint}/${this.partnerID}/${
        this.productID
      }/${this.trimLeadingSlash(path)}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          ...DEFAULT_HEADERS,
        },
      }
    );
    return this.returnOrThrow(response);
  }

  async patch(path: string, body?: unknown) {
    const response = await this.fetchWithRetry(
      `${this.endpoint}/${this.partnerID}/${
        this.productID
      }/${this.trimLeadingSlash(path)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: {
          ...DEFAULT_HEADERS,
        },
      }
    );
    return this.returnOrThrow(response);
  }

  async delete(path: string) {
    const response = await this.fetchWithRetry(
      `${this.endpoint}/${this.partnerID}/${
        this.productID
      }/${this.trimLeadingSlash(path)}`,
      {
        method: 'DELETE',
      }
    );
    return this.returnOrThrow(response);
  }
}

export { API };
